/**
 * SE-1883: the per-rotation detail dialog.
 *
 * ONE surface, TWO objects with opposite behavior. This dialog is where
 * a rotation's Directors and Members are edited side-by-side, and it's
 * the surface the ticket calls out as the documented failure mode: the
 * first pass had membership save copy reading like a grant. This file
 * imports its copy from `rotationCopy.ts` — do NOT inline "next
 * sign-in", "immediately", "grants nothing" strings here, or the
 * membership-vs-grant test will (rightly) miss the drift.
 *
 * Two data reads:
 *   - `useRoleScopes` filtered to this rotation's scope_id — the
 *     directors of THIS rotation. Same wire shape as the global grants
 *     table, narrowed to one scope.
 *   - `usePersonGroupMembers(groupId)` — the current active members.
 *
 * Two writes, on opposite planes:
 *   - Director assign / revoke → `PUT /api/user-access`, the T4
 *     grant plane. Uses `runSave` from the parent (preserve-current-
 *     flat-grants + ADMIN-provenance filter).
 *   - Member add / remove → `PATCH /api/person-groups/{id}/members`,
 *     the T13 NAMED-delta door (SE-1819). Never PUT.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useTranslation } from "react-i18next";

import { RotationNetworkSelect, offersNetworkChoice } from "./RotationNetworkSelect";
import type { ProgramNetwork } from "./simplAuthAPI";

import {
  usePersonGroupCandidates,
  usePersonGroupMembers,
  useRoleScopes,
} from "./hooks";
import {
  Director,
  PersonGroup,
  PersonGroupMember,
  RoleScope,
  ScopeBinding,
  dedupePeople,
  groupByDirector,
  apiErrorMessage,
  isAdminOwned,
  patchPersonGroupMembers,
  personDisplayName,
} from "./simplAuthAPI";
import {
  directorshipCopy,
  membershipCopy,
  refusalCopy,
  rotationAccessCopy,
} from "./rotationCopy";

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export type RotationDetailDialogProps = {
  rotation: PersonGroup;
  managedRoles: string[];
  /** The program's Attendings and Rotation Directors (director-picker candidates). */
  directorCandidates: {
    id: string;
    email: string;
    displayName: string;
  }[];
  /** Callback to run a director assign / revoke. Reuses the parent's
   *  preserve-current-flat-grants save flow so the two paths cannot
   *  drift. Returns a discriminated result — this dialog paints its own
   *  banner rather than reaching into the parent's state, so a modal
   *  action's outcome lands where the user is looking. */
  runDirectorSave: (input: {
    directorEmail: string;
    directorName: string;
    role: string;
    change: { add?: ScopeBinding[]; remove?: string[] };
    kind: "assign" | "revoke";
    rotationName: string;
    scopeIdForBadge?: string;
  }) => Promise<{ ok: true } | { ok: false; message: string }>;
  /** Called when the user clicks Rename → opens the parent's rename dialog. */
  onRequestRename: (rotation: PersonGroup) => void;
  /** Called when the user clicks Archive → opens the parent's archive confirm. */
  onRequestArchive: (rotation: PersonGroup) => void;
  onClose: () => void;
  /** Non-null when the parent is running a directorship save; used to
   *  disable per-row buttons on the directors table. */
  directorSaveInFlight: string | null;
  /**
   * The caller administers this rotation's program. False for a Rotation
   * Director, who may only add / remove members: rename, archive and the
   * directors section are hidden (and refused server-side).
   */
  canAdminister: boolean;
  /** SE-2832: the program's networks, as listed with its rotations. */
  networks?: ProgramNetwork[];
  /** SE-2832: save the rotation's network (null = the whole program). */
  onChangeNetwork?: (
    rotation: PersonGroup,
    networkId: string | null,
  ) => Promise<boolean>;
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export const RotationDetailDialog = ({
  rotation,
  managedRoles,
  directorCandidates,
  runDirectorSave,
  onRequestRename,
  onRequestArchive,
  onClose,
  directorSaveInFlight,
  canAdminister,
  networks = [],
  onChangeNetwork,
}: RotationDetailDialogProps) => {
  const { t } = useTranslation();
  const archived = rotation.archived_at !== null;
  const [networkSaving, setNetworkSaving] = useState(false);
  const showNetwork =
    canAdminister && !archived && !!onChangeNetwork && offersNetworkChoice(networks);

  // --- Directors: use useRoleScopes with scopeIds pinned to this rotation.
  //     `groupByDirector` folds the rows; we then read
  //     directors[i].rotations (which for this scoped view all describe
  //     the current rotation).
  const grants = useRoleScopes(
    managedRoles,
    canAdminister ? "person_group" : null,
    canAdminister ? rotation.program_id ?? null : null,
  );
  const rotationDirectors: Director[] = useMemo(() => {
    if (!grants.data) return [];
    const scopedRows = grants.data.role_scopes.filter(
      (r) => r.scope_id === rotation.id,
    );
    return groupByDirector(scopedRows);
  }, [grants.data, rotation.id]);

  // --- Members
  const members = usePersonGroupMembers(rotation.id);

  // --- Member candidates picker
  const candidates = usePersonGroupCandidates(rotation.id);

  // The current-members set, so the picker can hide people already in
  // the rotation. The `dedupePeople` join makes the "twice in the
  // roster appears once in every picker" AC honest.
  const currentMemberIds = useMemo(() => {
    const ids = new Set<string>();
    for (const m of members.data?.members ?? []) ids.add(m.person_id);
    return ids;
  }, [members.data]);

  const addablePeople = useMemo(() => {
    const rows = (candidates.data?.candidates ?? []).filter(
      (c) => !currentMemberIds.has(c.person_id),
    );
    return dedupePeople(rows);
  }, [candidates.data, currentMemberIds]);

  // --- Save state for the two planes.
  //     Kept per-plane on purpose: a director save must not spin the
  //     member controls, and vice versa. The one-alert bar at the top
  //     is shared because the user sees one action at a time.
  const [memberSaving, setMemberSaving] = useState<string | null>(null);
  // Per-section banners so a directorship result lives above the DESIGNATE
  // input (where the admin was just looking) and a membership result lives
  // above the ADD input, instead of a shared bar at the dialog top.
  type Banner = { severity: "success" | "error" | "info"; message: string };
  const [directorBanner, setDirectorBanner] = useState<Banner | null>(null);
  const [memberBanner, setMemberBanner] = useState<Banner | null>(null);

  // --- Add-member picker state
  const [personToAdd, setPersonToAdd] = useState<
    { person_id: string; label: string; role: string | null } | null
  >(null);

  const personOptions = useMemo(
    () =>
      addablePeople.map((p) => ({
        person_id: p.person_id,
        label: personDisplayName(p),
        role: p.person_role as string | null,
      })),
    [addablePeople],
  );

  // If the selected value disappears from the options after a save
  // reload (they were just added, or the sync ate them), drop it.
  useEffect(() => {
    if (!personToAdd) return;
    if (!personOptions.find((o) => o.person_id === personToAdd.person_id)) {
      setPersonToAdd(null);
    }
  }, [personOptions, personToAdd]);

  const addMember = async () => {
    if (!personToAdd || archived) return;
    setMemberSaving(personToAdd.person_id);
    setMemberBanner(null);
    try {
      const res = await patchPersonGroupMembers(rotation.id, {
        add: [personToAdd.person_id],
      });
      if (res.added > 0) {
        setMemberBanner({
          severity: "success",
          message: membershipCopy.memberAdded,
        });
      } else {
        // Idempotent — already present.
        setMemberBanner({
          severity: "info",
          message: t("That person is already a member of this rotation."),
        });
      }
      setPersonToAdd(null);
      members.reload();
    } catch (err: unknown) {
      // Log the raw error for triage; surface a plain-language line to
      // the user. The service returns 409 on genuine concurrent moves;
      // everything else is "something went wrong, we reloaded".
      // eslint-disable-next-line no-console
      console.error("addMember failed", err);
      members.reload();
      const status =
        (err as { response?: { status?: number }; status?: number })?.response
          ?.status ??
        (err as { status?: number })?.status;
      const generic = t(
        "Something went wrong adding that member. The latest is shown now — please try again.",
      );
      setMemberBanner({
        severity: "error",
        message:
          status === 409
            ? refusalCopy.staleWrite
            : status === 403 || status === 422
              ? apiErrorMessage(err, generic)
              : generic,
      });
    } finally {
      setMemberSaving(null);
    }
  };

  const removeMember = async (member: PersonGroupMember) => {
    if (archived) return;
    setMemberSaving(member.person_id);
    setMemberBanner(null);
    try {
      const res = await patchPersonGroupMembers(rotation.id, {
        remove: [member.person_id],
      });
      if (res.archived > 0) {
        setMemberBanner({
          severity: "success",
          message: membershipCopy.memberRemoved,
        });
      } else if (res.not_found.length > 0) {
        // The membership vanished between render and click — the
        // stale-view signal the SE-1883 AC ("a write composed from
        // state that has since changed") wants surfaced honestly.
        setMemberBanner({
          severity: "info",
          message: membershipCopy.someAlreadyGone(res.not_found.length),
        });
      }
      members.reload();
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error("removeMember failed", err);
      members.reload();
      const status =
        (err as { response?: { status?: number }; status?: number })?.response
          ?.status ??
        (err as { status?: number })?.status;
      const generic = t(
        "Something went wrong removing that member. The latest is shown now — please try again.",
      );
      setMemberBanner({
        severity: "error",
        message:
          status === 409
            ? refusalCopy.staleWrite
            : status === 403
              ? apiErrorMessage(err, generic)
              : generic,
      });
    } finally {
      setMemberSaving(null);
    }
  };

  // Directors who are not members (SE-1883), by lowercased email.
  // A member's person_id is the user id, as is a director candidate's
  // id. A director with no candidate row has no Attending membership in
  // this program, so can't be a member either.
  const membersData = members.data?.members ?? [];
  const directorsNotMembers = useMemo(() => {
    const memberIds = new Set(membersData.map((m) => m.person_id));
    const out = new Set<string>();
    for (const d of rotationDirectors) {
      const email = d.email.toLowerCase();
      const userId = directorCandidates.find(
        (c) => c.email.toLowerCase() === email,
      )?.id;
      if (!userId || !memberIds.has(userId)) out.add(email);
    }
    return out;
  }, [rotationDirectors, directorCandidates, membersData]);

  // --- Director assign form (inline in the directors section)
  const [assignDirectorEmail, setAssignDirectorEmail] = useState<string>("");

  const existingDirectorEmails = useMemo(
    () => new Set(rotationDirectors.map((d) => d.email.toLowerCase())),
    [rotationDirectors],
  );

  const availableDirectorOptions = useMemo(
    () =>
      directorCandidates.filter(
        (c) => !existingDirectorEmails.has(c.email.toLowerCase()),
      ),
    [directorCandidates, existingDirectorEmails],
  );

  const [directorAssignInFlight, setDirectorAssignInFlight] = useState(false);
  const onAssignDirector = async () => {
    if (!assignDirectorEmail || archived) return;
    const candidate = directorCandidates.find(
      (c) => c.email.toLowerCase() === assignDirectorEmail.toLowerCase(),
    );
    if (!candidate) return;
    // Just this rotation: `runDirectorSave` reads the director's other
    // rotations fresh and carries them (SE-2841).
    setDirectorAssignInFlight(true);
    setDirectorBanner(null);
    const res = await runDirectorSave({
      directorEmail: candidate.email,
      directorName: candidate.displayName,
      role: managedRoles[0] ?? "RotationDirector",
      change: { add: [{ scope_id: rotation.id, scope_name: rotation.label }] },
      kind: "assign",
      rotationName: rotation.label,
    });
    setDirectorAssignInFlight(false);
    if (res.ok) {
      setDirectorBanner({
        severity: "success",
        message: directorshipCopy.directorAssigned(
          candidate.displayName,
          rotation.label,
        ),
      });
      setAssignDirectorEmail("");
      grants.reload();
    } else {
      setDirectorBanner({ severity: "error", message: res.message });
    }
  };

  const onRevokeDirector = async (director: Director, row: RoleScope) => {
    if (!isAdminOwned(row.data_source_name) || !row.role || archived) return;
    setDirectorBanner(null);
    const displayName =
      directorCandidates.find(
        (c) => c.email.toLowerCase() === director.email.toLowerCase(),
      )?.displayName ?? director.email;
    const res = await runDirectorSave({
      directorEmail: director.email,
      directorName: displayName,
      role: row.role,
      change: { remove: [row.scope_id] },
      kind: "revoke",
      rotationName: rotation.label,
      scopeIdForBadge: row.scope_id,
    });
    if (res.ok) {
      setDirectorBanner({
        severity: "success",
        message: directorshipCopy.directorRevoked(displayName, rotation.label),
      });
      grants.reload();
    } else {
      setDirectorBanner({ severity: "error", message: res.message });
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="md"
      data-testid="rotation-detail-dialog"
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          {rotation.label}
          {archived && (
            <Chip
              size="small"
              label={t("Archived")}
              sx={{ ml: 1 }}
              color="default"
            />
          )}
        </Typography>
        {!archived && canAdminister && (
          <>
            <Tooltip title={t("Rename")}>
              <IconButton
                size="small"
                onClick={() => onRequestRename(rotation)}
                data-testid="rotation-detail-rename-button"
                aria-label={t("Rename rotation")}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("Archive")}>
              <IconButton
                size="small"
                onClick={() => onRequestArchive(rotation)}
                data-testid="rotation-detail-archive-button"
                aria-label={t("Archive rotation")}
              >
                <ArchiveOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        <Tooltip title={t("Close")}>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t("Close")}
            data-testid="rotation-detail-close-button"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {archived && (
            <Alert severity="info">
              {t(
                "This rotation is archived. It stays visible for reference; membership and director changes are disabled.",
              )}
            </Alert>
          )}

          {/* ================= NETWORK (SE-2832) ================= */}
          {showNetwork && (
            <Box data-testid="rotation-detail-network-section">
              <RotationNetworkSelect
                networks={networks}
                value={rotation.network_id ?? null}
                disabled={networkSaving}
                testId="rotation-detail-network"
                onChange={async (networkId) => {
                  if (networkId === (rotation.network_id ?? null)) return;
                  setNetworkSaving(true);
                  await onChangeNetwork!(rotation, networkId);
                  setNetworkSaving(false);
                }}
              />
            </Box>
          )}

          {/* ================= DIRECTORS ================= */}
          {canAdminister && (
          <Section
            title={t("Directors")}
            testId="rotation-detail-directors-section"
          >
            <Alert
              severity="info"
              variant="outlined"
              data-testid="rotation-detail-directorship-disclosure"
            >
              {directorshipCopy.disclosureBanner}
            </Alert>

            {directorBanner && (
              <Alert
                severity={directorBanner.severity}
                onClose={() => setDirectorBanner(null)}
                data-testid="rotation-detail-director-banner"
              >
                {directorBanner.message}
              </Alert>
            )}

            {/* Designate a director inline — at the top of the section so
                the admin's next action is closest to the notice that
                describes what it does. Mirrors the Members layout below. */}
            {!archived && (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ sm: "flex-end" }}
              >
                <Autocomplete
                  size="small"
                  options={availableDirectorOptions}
                  getOptionLabel={(o) => `${o.displayName} <${o.email}>`}
                  isOptionEqualToValue={(a, b) => a.email === b.email}
                  value={
                    directorCandidates.find(
                      (c) =>
                        c.email.toLowerCase() ===
                        assignDirectorEmail.toLowerCase(),
                    ) ?? null
                  }
                  onChange={(_e, v) =>
                    setAssignDirectorEmail(v?.email ?? "")
                  }
                  sx={{ flex: 1, minWidth: 0 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("Designate a director")}
                      // No helperText here — the disclosure banner
                      // above already anchors "grants dashboard access"
                      // and the effective-time semantics; a duplicate
                      // helperText line also throws off flex-end
                      // alignment against the DESIGNATE button.
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        shrink: true,
                      }}
                      inputProps={{
                        ...params.inputProps,
                        "data-testid":
                          "rotation-detail-director-assign-picker",
                      }}
                    />
                  )}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={onAssignDirector}
                  disabled={!assignDirectorEmail || directorAssignInFlight}
                  data-testid="rotation-detail-director-assign-button"
                  sx={{ flexShrink: 0, height: 40 }}
                  startIcon={
                    directorAssignInFlight ? (
                      <CircularProgress size={16} />
                    ) : undefined
                  }
                >
                  {t("DESIGNATE")}
                </Button>
              </Stack>
            )}
            {!archived && (
              <Typography variant="caption" color="text.secondary">
                {t(rotationAccessCopy.directorCandidatesAreAttendings)}
              </Typography>
            )}

            {grants.status === "loading" && (
              <Loading text={t("Loading directors…")} />
            )}
            {grants.status === "failed" && (
              <Alert severity="error">
                {t("Couldn't load this rotation's directors.")}
              </Alert>
            )}
            {grants.status === "succeeded" && (
              <>
                {rotationDirectors.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    data-testid="rotation-detail-no-directors"
                  >
                    {t(
                      "No directors yet — nobody currently has a narrowed dashboard view of this rotation.",
                    )}
                  </Typography>
                ) : (
                  <TableContainer component={PaperLike}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("Director")}</TableCell>
                          <TableCell>{t("Source")}</TableCell>
                          <TableCell width={64} align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rotationDirectors.flatMap((d) =>
                          d.rotations.map((row) => {
                            const admin = isAdminOwned(row.data_source_name);
                            const displayName =
                              directorCandidates.find(
                                (c) =>
                                  c.email.toLowerCase() ===
                                  d.email.toLowerCase(),
                              )?.displayName ?? d.email;
                            const isSaving =
                              directorSaveInFlight ===
                              `${d.email}::${row.scope_id}`;
                            return (
                              <TableRow key={`${d.email}::${row.scope_id}`}>
                                <TableCell>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <Typography variant="body2">
                                      {displayName}
                                    </Typography>
                                    {directorsNotMembers.has(
                                      d.email.toLowerCase(),
                                    ) && (
                                      <Tooltip
                                        title={t(
                                          directorshipCopy.directorNotMemberTooltip,
                                        )}
                                      >
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          label={t(
                                            directorshipCopy.directorNotMemberLabel,
                                          )}
                                          data-testid="rotation-detail-director-not-member"
                                        />
                                      </Tooltip>
                                    )}
                                  </Stack>
                                  {displayName !== d.email && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {d.email}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {admin ? (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {t("Set here")}
                                    </Typography>
                                  ) : (
                                    <Chip
                                      size="small"
                                      label={t("Synced")}
                                      variant="outlined"
                                    />
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {admin && !archived ? (
                                    <Tooltip title={t("Revoke")}>
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            onRevokeDirector(d, row)
                                          }
                                          disabled={
                                            directorSaveInFlight !== null
                                          }
                                          data-testid={
                                            "rotation-detail-revoke-director"
                                          }
                                          aria-label={t(
                                            "Revoke director {{name}}",
                                            { name: displayName },
                                          )}
                                        >
                                          {isSaving ? (
                                            <CircularProgress size={16} />
                                          ) : (
                                            <DeleteOutlineIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <Tooltip
                                      title={
                                        archived
                                          ? t(
                                              "Archived rotations don't accept changes.",
                                            )
                                          : t(
                                              "This directorship was written by another system; manage it there.",
                                            )
                                      }
                                    >
                                      <span>
                                        <IconButton size="small" disabled>
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          }),
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

          </Section>
          )}

          {/* ================= MEMBERS ================= */}
          <Section
            title={t("Members")}
            testId="rotation-detail-members-section"
          >
            <Alert
              severity="info"
              variant="outlined"
              data-testid="rotation-detail-membership-disclosure"
            >
              {membershipCopy.disclosureBanner}
            </Alert>

            {memberBanner && (
              <Alert
                severity={memberBanner.severity}
                onClose={() => setMemberBanner(null)}
                data-testid="rotation-detail-member-banner"
              >
                {memberBanner.message}
              </Alert>
            )}

            {/* Add member inline — at the top of the section so the
                admin's next action is closest to the notice that
                describes what it does. */}
            {!archived && (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ sm: "flex-end" }}
              >
                <Autocomplete
                  size="small"
                  options={personOptions}
                  getOptionLabel={(o) =>
                    o.role ? `${o.label} (${o.role})` : o.label
                  }
                  isOptionEqualToValue={(a, b) => a.person_id === b.person_id}
                  value={personToAdd}
                  onChange={(_e, v) => setPersonToAdd(v)}
                  disabled={candidates.status !== "succeeded"}
                  loading={candidates.status === "loading"}
                  sx={{ flex: 1, minWidth: 0 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("Add a member")}
                      helperText={
                        candidates.status === "succeeded" &&
                        personOptions.length === 0
                          ? t(
                              "Every Attending in the program is already a member of this rotation.",
                            )
                          : undefined
                      }
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        shrink: true,
                      }}
                      inputProps={{
                        ...params.inputProps,
                        "data-testid": "rotation-detail-member-add-picker",
                      }}
                    />
                  )}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={addMember}
                  disabled={!personToAdd || memberSaving !== null}
                  data-testid="rotation-detail-member-add-button"
                  sx={{ flexShrink: 0, height: 40 }}
                  startIcon={
                    memberSaving === personToAdd?.person_id ? (
                      <CircularProgress size={16} />
                    ) : undefined
                  }
                >
                  {t("ADD")}
                </Button>
              </Stack>
            )}
            {!archived && (
              <Typography variant="caption" color="text.secondary">
                {t(rotationAccessCopy.memberCandidatesAreAttendings)}
              </Typography>
            )}

            {members.status === "loading" && (
              <Loading text={t("Loading members…")} />
            )}
            {members.status === "failed" && (
              <Alert severity="error">
                {t("Couldn't load this rotation's members.")}
              </Alert>
            )}
            {members.status === "succeeded" && (
              <>
                {membersData.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    data-testid="rotation-detail-no-members"
                  >
                    {t(
                      "No members yet. Add someone above — membership takes effect immediately.",
                    )}
                  </Typography>
                ) : (
                  <TableContainer component={PaperLike}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("Person")}</TableCell>
                          <TableCell>{t("Role")}</TableCell>
                          <TableCell>{t("Source")}</TableCell>
                          <TableCell width={64} align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {membersData.map((m) => {
                          const isSaving = memberSaving === m.person_id;
                          const isSynced = m.data_source_name !== null;
                          return (
                            <TableRow key={m.person_id}>
                              <TableCell>{personDisplayName(m)}</TableCell>
                              <TableCell>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {m.person_role ?? "—"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {isSynced ? (
                                  <Tooltip
                                    title={membershipCopy.syncedMembershipRemovalDisclosure(
                                      m.data_source_name ?? "",
                                    )}
                                  >
                                    <Chip
                                      size="small"
                                      label={t("Synced ({{src}})", {
                                        src: m.data_source_name,
                                      })}
                                      variant="outlined"
                                    />
                                  </Tooltip>
                                ) : (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {t("Set here")}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {!archived ? (
                                  <Tooltip
                                    title={
                                      isSynced
                                        ? membershipCopy.syncedMembershipRemovalDisclosure(
                                            m.data_source_name ?? "",
                                          )
                                        : t("Remove from rotation")
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => removeMember(m)}
                                        disabled={memberSaving !== null}
                                        data-testid="rotation-detail-remove-member"
                                        aria-label={t(
                                          "Remove {{name}} from rotation",
                                          { name: personDisplayName(m) },
                                        )}
                                      >
                                        {isSaving ? (
                                          <CircularProgress size={16} />
                                        ) : (
                                          <DeleteOutlineIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <IconButton size="small" disabled>
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

          </Section>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="rotation-detail-done-button">
          {t("Done")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// -----------------------------------------------------------------------------
// Small helpers kept local — this dialog is its own file and the pieces
// don't need to bleed into anywhere else.
// -----------------------------------------------------------------------------

const Section = ({
  title,
  testId,
  children,
}: {
  title: string;
  testId: string;
  children: React.ReactNode;
}) => (
  <Box data-testid={testId}>
    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
      {title}
    </Typography>
    <Stack spacing={1.5}>{children}</Stack>
  </Box>
);

const Loading = ({ text }: { text: string }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <CircularProgress size={16} />
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </Box>
);

// A "paper-like" component-prop for TableContainer, plain enough to
// match the flat surface language used elsewhere on the page.
const PaperLike = ({ children }: { children?: React.ReactNode }) => (
  <Box
    sx={{
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      overflow: "hidden",
      "& .MuiTableCell-root": { py: 0.75 },
      "& .MuiTableCell-head": { fontWeight: 500 },
    }}
  >
    {children}
  </Box>
);
